import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Upload, Loader2, CheckCircle, AlertTriangle, FileText, Download, Info } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminImportUsers = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/auth/import-csv`, formData);
      setResult(res.data);
      toast({
        title: language === 'es' ? 'Importacion completada' : 'Import completed',
        description: `${res.data.imported} ${language === 'es' ? 'usuarios importados' : 'users imported'}`,
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'Import failed',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'nombre,email,empresa,telefono,ciudad,pais,region,descripcion,propuesta_valor,categoria_desafio,estado_desarrollo,sitio_web,cargo\nJuan Perez,juan.perez@gmail.com,Mi Startup SPA,+56912345678,Santiago,Chile,Metropolitana,Plataforma de gestion,Automatizar procesos,Tecnologia,MVP,www.mistartup.cl,CEO\nMaria Gonzalez,maria.gonzalez@hotmail.com,Innovacion Chile,+56987654321,Valparaiso,Chile,Valparaiso,App de salud,,Salud,Idea,,\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_postulantes.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/dashboard"
          className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 mb-4"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'es' ? 'Volver al Panel' : 'Back to Dashboard'}
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="page-title">
          {language === 'es' ? 'Importar Postulantes' : 'Import Applicants'}
        </h1>
        <p className="text-gray-500 text-sm mb-8">
          {language === 'es'
            ? 'Importa usuarios de tipo postulante desde un archivo CSV.'
            : 'Import applicant users from a CSV file.'}
        </p>

        {/* Instructions Card */}
        <Card className="border-2 border-blue-200 bg-blue-50 mb-6">
          <CardContent className="p-5">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-bold text-blue-900 mb-2">
                  {language === 'es' ? 'Formato del archivo CSV' : 'CSV file format'}
                </h3>
                <p className="text-sm text-blue-800 mb-3">
                  {language === 'es'
                    ? 'El archivo debe ser un CSV con codificacion UTF-8. La primera fila debe contener los nombres de las columnas.'
                    : 'The file must be a UTF-8 encoded CSV. The first row must contain column names.'}
                </p>
                <div className="bg-white rounded-lg p-3 mb-3 font-mono text-xs text-gray-700 border border-blue-200 overflow-x-auto">
                  <div className="text-blue-600 font-bold">nombre,email,empresa,telefono,ciudad,pais,region,descripcion,propuesta_valor,categoria_desafio,estado_desarrollo,sitio_web,cargo</div>
                  <div>Juan Perez,juan@gmail.com,Mi Startup,+569123...,Santiago,Chile,Metropolitana,Plataforma de gestion,Automatizar procesos,Tecnologia,MVP,www.mi.cl,CEO</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-blue-800 mb-3">
                  <p><span className="font-bold text-red-600">*</span> <strong>nombre</strong> — {language === 'es' ? 'Nombre completo' : 'Full name'}</p>
                  <p><span className="font-bold text-red-600">*</span> <strong>email</strong> — {language === 'es' ? 'Email unico (login)' : 'Unique email (login)'}</p>
                  <p><strong>empresa</strong> — {language === 'es' ? 'Empresa o startup' : 'Company'}</p>
                  <p><strong>telefono</strong> — {language === 'es' ? 'Telefono' : 'Phone'}</p>
                  <p><strong>ciudad</strong> — {language === 'es' ? 'Ciudad' : 'City'}</p>
                  <p><strong>pais</strong> — {language === 'es' ? 'Pais' : 'Country'}</p>
                  <p><strong>region</strong> — {language === 'es' ? 'Region' : 'Region'}</p>
                  <p><strong>descripcion</strong> — {language === 'es' ? 'Descripcion del proyecto' : 'Project description'}</p>
                  <p><strong>propuesta_valor</strong> — {language === 'es' ? 'Propuesta de valor' : 'Value proposition'}</p>
                  <p><strong>categoria_desafio</strong> — {language === 'es' ? 'Categoria del desafio' : 'Challenge category'}</p>
                  <p><strong>estado_desarrollo</strong> — {language === 'es' ? 'Ej: Idea, MVP, Growth' : 'E.g.: Idea, MVP, Growth'}</p>
                  <p><strong>sitio_web</strong> — {language === 'es' ? 'URL del sitio web' : 'Website URL'}</p>
                  <p><strong>cargo</strong> — {language === 'es' ? 'Cargo en la empresa' : 'Job title'}</p>
                </div>
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                  {language === 'es'
                    ? 'Todos los usuarios importados recibiran la contraseña temporal: Colabora2026! — Los emails duplicados seran ignorados automaticamente.'
                    : 'All imported users will receive the temporary password: Colabora2026! — Duplicate emails will be automatically skipped.'}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 text-blue-700 border-blue-300 hover:bg-blue-100"
                  onClick={downloadTemplate}
                  data-testid="download-template"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  {language === 'es' ? 'Descargar plantilla CSV' : 'Download CSV template'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card className="border-2 border-dashed border-gray-300 hover:border-purple-400 transition-colors mb-6">
          <CardContent className="p-8">
            <div
              className="text-center cursor-pointer"
              onClick={() => fileRef.current?.click()}
              data-testid="upload-area"
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-purple-600" />
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">
                    {language === 'es' ? 'Haz clic para seleccionar archivo CSV' : 'Click to select CSV file'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {language === 'es' ? 'Solo archivos .csv' : 'Only .csv files'}
                  </p>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileChange}
                data-testid="csv-file-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Import Button */}
        <Button
          onClick={handleImport}
          disabled={!file || uploading}
          className="w-full text-white mb-6"
          style={{ backgroundColor: '#6949b6' }}
          data-testid="import-btn"
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {language === 'es' ? 'Importando...' : 'Importing...'}</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> {language === 'es' ? 'Importar Postulantes' : 'Import Applicants'}</>
          )}
        </Button>

        {/* Results */}
        {result && (
          <Card className="border border-gray-200" data-testid="import-results">
            <CardContent className="p-5">
              <h3 className="text-base font-bold text-gray-900 mb-4">
                {language === 'es' ? 'Resultado de la importacion' : 'Import results'}
              </h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-green-700">{result.imported}</p>
                  <p className="text-xs text-green-600">{language === 'es' ? 'Importados' : 'Imported'}</p>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-yellow-700">{result.skipped}</p>
                  <p className="text-xs text-yellow-600">{language === 'es' ? 'Omitidos' : 'Skipped'}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-gray-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-700">{result.total_rows}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
              </div>
              {result.imported > 0 && (
                <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded mb-3">
                  {language === 'es'
                    ? `Contraseña temporal asignada: ${result.default_password}`
                    : `Temporary password assigned: ${result.default_password}`}
                </p>
              )}
              {result.errors && result.errors.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    {language === 'es' ? 'Detalle de omitidos (max 50):' : 'Skipped details (max 50):'}
                  </p>
                  <div className="max-h-40 overflow-y-auto bg-gray-50 rounded p-2 text-xs text-gray-600 space-y-1">
                    {result.errors.map((err, i) => (
                      <div key={i}>
                        {err.row && `Fila ${err.row}: `}{err.email && `${err.email} — `}{err.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminImportUsers;
